import type { ModelConfig } from '$/domains/workFlowV3/type';

export enum TVFlowNodeNextTypeEnum {
  IMAGE_NODE = 'IMAGE_NODE',
  AUDIO_NODE = 'AUDIO_NODE',
  TEXT_NODE = 'TEXT_NODE',
  VIDEO_NODE = 'VIDEO_NODE',
  SCREENPLAY_NODE = 'SCREENPLAY_NODE', // 剧本
  SCRIPT_NODE = 'SCRIPT_NODE', // 脚本
  DIRECTOR_CONSOLE = 'DIRECTOR_CONSOLE', // 导演台
  CONTAINER_NODE = 'CONTAINER_NODE', // 容器（可嵌套）
}

export const NODE_NAME_CONFIG = {
  [TVFlowNodeNextTypeEnum.TEXT_NODE]: 'text_node',
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: 'image_node',
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: 'video_node',
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: 'audio_node',
  [TVFlowNodeNextTypeEnum.SCREENPLAY_NODE]: 'script_1',
  [TVFlowNodeNextTypeEnum.SCRIPT_NODE]: 'script',
  [TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE]: 'director_s_booth',
  [TVFlowNodeNextTypeEnum.CONTAINER_NODE]: '容器',
};

export type ModelNodeType =
  | TVFlowNodeNextTypeEnum.IMAGE_NODE
  | TVFlowNodeNextTypeEnum.VIDEO_NODE
  | TVFlowNodeNextTypeEnum.AUDIO_NODE
  | TVFlowNodeNextTypeEnum.TEXT_NODE
  | TVFlowNodeNextTypeEnum.SCRIPT_NODE;

export const NODE_TYPE_TO_MODEL_TYPE_MAP: Record<ModelNodeType, keyof ModelConfig> = {
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: 'image',
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: 'video',
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: 'audio',
  [TVFlowNodeNextTypeEnum.TEXT_NODE]: 'text',
  [TVFlowNodeNextTypeEnum.SCRIPT_NODE]: 'script',
};

export interface INodeAddPanelConfig {
  icon: string;
  type: TVFlowNodeNextTypeEnum;
  text: string;
  keyword: string;
  badge?: string;
}

export const NODE_ADD_PANEL_CONFIG: Partial<Record<TVFlowNodeNextTypeEnum, INodeAddPanelConfig>> = {
  [TVFlowNodeNextTypeEnum.TEXT_NODE]: {
    icon: 'textAlignmentLeftBold',
    type: TVFlowNodeNextTypeEnum.TEXT_NODE,
    text: 'text_node', // 文本
    keyword: 'scripts_ad_copy_brand_copy_', // 脚本、广告词、品牌文案。
  },
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: {
    icon: 'pictureBold',
    type: TVFlowNodeNextTypeEnum.IMAGE_NODE,
    text: 'image_node', // 图片
    keyword: 'posters_storyboards_and_character_design_', // 海报、分镜、角色设计。
  },
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: {
    icon: 'videoFileBold',
    type: TVFlowNodeNextTypeEnum.VIDEO_NODE,
    text: 'video_node', // 视频
    keyword: 'creative_arts_advertising_and_animated_films_', // 创意、广告、动画电影。
  },
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: {
    icon: 'musicV2Bold',
    type: TVFlowNodeNextTypeEnum.AUDIO_NODE,
    text: 'audio_node', // 音频
    keyword: 'sound_effects_background_music_music_', // 音效、配乐、音乐。
  },
  [TVFlowNodeNextTypeEnum.SCREENPLAY_NODE]: {
    icon: 'bookBold',
    type: TVFlowNodeNextTypeEnum.SCREENPLAY_NODE,
    text: 'script_1',
    keyword: 'generate_professional_scripts_through_the_script',
    badge: 'New',
  },
  [TVFlowNodeNextTypeEnum.SCRIPT_NODE]: {
    icon: 'fileTextBold',
    type: TVFlowNodeNextTypeEnum.SCRIPT_NODE,
    text: 'script', // 脚本
    keyword: 'storyboard_script_generate_storyboard_', // 分镜脚本、生成故事板。
  },
  [TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE]: {
    icon: 'box3dTopBold',
    type: TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE,
    text: 'director_s_booth',
    keyword: 'constructing_spatial_relationships_for_precise_control_over_the_composition',
    badge: 'New',
  },
};

export const NODE_ADD_PANEL_ITEMS: INodeAddPanelConfig[] = [
  NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
  NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
  NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
  NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.AUDIO_NODE],
  NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCREENPLAY_NODE],
  NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCRIPT_NODE],
  NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE],
].filter((item): item is INodeAddPanelConfig => Boolean(item));

export const NODE_LINE_CONFIG = {
  // 文本
  [TVFlowNodeNextTypeEnum.TEXT_NODE]: {
    input: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
    ],
    output: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.AUDIO_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCRIPT_NODE],
    ],
  },
  // 图片
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: {
    input: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE],
    ],
    output: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCRIPT_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE],
    ],
  },
  // 视频
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: {
    input: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCREENPLAY_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.AUDIO_NODE],
    ],
    output: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCRIPT_NODE],
    ],
  },
  // 音频
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: {
    input: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
    ],
    output: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCRIPT_NODE],
    ],
  },
  // 剧本不接收文本或其他剧本输入，右侧仅允许连接视频和脚本。
  [TVFlowNodeNextTypeEnum.SCREENPLAY_NODE]: {
    input: [],
    output: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCRIPT_NODE],
    ],
  },
  // 脚本
  [TVFlowNodeNextTypeEnum.SCRIPT_NODE]: {
    input: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.SCREENPLAY_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE],
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.AUDIO_NODE],
    ],
  },
  // 导演台：单输入约束在 use-editor-props.canAddLine 中处理，这里只声明类型白名单。
  [TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE]: {
    input: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
    ],
    output: [
      NODE_ADD_PANEL_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE],
    ],
  },
};

/** input_parameters 中 type 字段与节点类型的映射 */
export const INPUT_TYPE_TO_NODE_TYPE: Record<string, TVFlowNodeNextTypeEnum> = {
  text: TVFlowNodeNextTypeEnum.TEXT_NODE,
  image: TVFlowNodeNextTypeEnum.IMAGE_NODE,
  video: TVFlowNodeNextTypeEnum.VIDEO_NODE,
  audio: TVFlowNodeNextTypeEnum.AUDIO_NODE,
};
